import React, {useCallback, useEffect, useRef, useState} from "react";
import { Helmet } from "react-helmet";
import {Route, Switch, useHistory, useLocation} from "react-router-dom";
import {connect} from "react-redux";
import * as PropTypes from "prop-types";
import { useTranslation } from 'react-i18next';
import useConfirmation from '../common/useConfirmation';
/* eslint-disable linebreak-style */
/* eslint-disable jsx-a11y/iframe-has-title */
/* eslint-disable react/jsx-filename-extension */
/* eslint-disable quotes */
import "../../styles/styles.scss";
import ManageNav from "./ManageNav";
import NotFoundPage from "../NotFoundPage";
import Teacher from "./Teacher";
import Client from "./Client";
import ClientLedger from "./ClientLedger";
import ClientNotificationHistory from "./ClientNotificationHistory";
import Dashboard from "./Dashboard";
import Settings from "./Settings";
import SupportReportsProvider from "../support/SupportReportsProvider";
import TeacherAccessProvider from './TeacherAccessProvider';
import SupportRoutes from "../support/SupportRoutes";
import SchoolCalendarReadPage from "../schoolCalendar/SchoolCalendarReadPage";
import SchoolCalendarEditor from "../schoolCalendar/SchoolCalendarEditor";
import calendarContextKey from "../schoolCalendar/calendarContext";
import Permissions from "./Permissions";
import Student from "./Student";
import TeacherPeopleProfile from "./TeacherPeopleProfile";
import People from "./People";
import SchoolClass from "./SchoolClass";
import SchoolClasses from "./SchoolClasses";
import AttendancePage from "./AttendancePage";
import Login from "./Login";
import ContextChoice from "../auth/ContextChoice";
import PasswordResetRequest from "./PasswordResetRequest";
import PasswordResetConfirm from "./PasswordResetConfirm";
import {ROLES as ROLE} from "../common/Const";
import * as userActions from "../../redux/actions/usersActions";
import Book from "./Book";
import Books from "./Books";
import Product from "./products/Product";
import Products from "./products/Products";
import Enrollment from "./enrollments/Enrollment";
import Enrollments from "./enrollments/Enrollments";
import HeaderNav from "../HeaderNav";
import AttendanceReport from "../report/AttendanceReport";
import ClientSettlementsReport from "../report/ClientSettlementsReport";
import * as settingsActions from "../../redux/actions/settingsActions";
import { buildSchoolYearOptions } from "../common/schoolYear";
import {
  buildSearchWithSchoolYear,
  getNormalizedSchoolYearStartYearFromSearch,
} from "./manageSchoolYearRouting";
import { PEOPLE_TABS } from "./peopleViewState";
import {
  canAccessManagePath,
  canManagePermissions,
  canManageSettings,
  getEffectivePermissionRole,
  canReadBooks,
  canReadClients,
  canReadPayments,
  canReadTeachers,
  isManageRole,
} from "./rbac";
import {
  clearLastSelectedContextKey,
  rememberLastSelectedContextKey,
} from "../../services/accountContextStorage";

export function ManagementPage({
  user,
  signOut,
  checkToken,
  getLoginContexts,
  selectLoginContext,
  activeSchoolYearStartYear,
  setActiveSchoolYearStartYear,
}) {
  const { confirm, confirmation } = useConfirmation();
  const { t: tr } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [switchChoice, setSwitchChoice] = useState(null);
  const [switchError, setSwitchError] = useState("");
  const calendarHasUnsavedChanges = useRef(false);
  const handleCalendarUnsavedChange = useCallback((value) => { calendarHasUnsavedChanges.current = value; }, []);
  const selectorOptions = buildSchoolYearOptions();
  const isClientPortalUser = Boolean(user && user.role === ROLE.CLIENT);
  const isAuthenticated = user && isManageRole(user);
  const hasUnsupportedRole = Boolean(
    user &&
    user.role !== ROLE.GUEST &&
    !isAuthenticated &&
    !isClientPortalUser,
  );
  const normalizedStartYear = getNormalizedSchoolYearStartYearFromSearch(location.search);
  const normalizedSearch = buildSearchWithSchoolYear(location.search, normalizedStartYear);
  const isSchoolYearHydrated = !isAuthenticated || (
    activeSchoolYearStartYear === normalizedStartYear
    && location.search === normalizedSearch
  );
  const menuItems = [
    { name: tr("common:dashboard"), route: "/manage/", icon: "fas fa-th-large" },
    { name: tr("common:people"), route: "/manage/people", icon: "fas fa-users" },
    { name: tr("common:classes"), route: "/manage/classes", icon: "far fa-calendar-alt" },
    { name: tr("common:schoolCalendar"), route: "/manage/school-calendar", icon: "far fa-calendar" },
    { name: tr("common:books"), route: "/manage/books", icon: "fas fa-book-open" },
    { name: tr("common:products"), route: "/manage/products", icon: "fas fa-tags" },
    { name: tr("common:enrollments"), route: "/manage/enrollments", icon: "fas fa-file-signature" },
    { name: tr("common:reports"), route: "/manage/reports", icon: "fas fa-chart-bar", exact: true },
    { name: tr("common:settlements"), route: "/manage/reports/client-settlements", icon: "far fa-money-bill-alt" },
    { name: tr("common:supportReports"), route: "/manage/support", icon: "far fa-comment-alt" },
  ];
  const roleMenuItems = menuItems.filter((item) => {
    if (!user) return false;
    if (item.route === "/manage/people") return true;
    if (item.route.startsWith("/manage/books")) return canReadBooks(user);
    if (item.route.startsWith("/manage/products")) return canReadPayments(user);
    if (item.route.startsWith("/manage/enrollments")) return canReadPayments(user);
    if (item.route.startsWith("/manage/reports/client-settlements")) return canReadPayments(user);
    return true;
  });
  const canAccessCurrentPath = isAuthenticated && canAccessManagePath(user, location.pathname);
  const isDashboardRoute = location.pathname === "/manage" || location.pathname === "/manage/";
  const effectivePermissionRole = getEffectivePermissionRole(user);

  useEffect(() => {
    if (!user || user.role === ROLE.GUEST) checkToken();
  }, [checkToken, user]);

  useEffect(() => {
    if (isClientPortalUser) {
      history.replace("/client");
    }
  }, [history, isClientPortalUser]);

  useEffect(() => {
    if (!isAuthenticated) return;

    if (normalizedStartYear !== activeSchoolYearStartYear) {
      setActiveSchoolYearStartYear(normalizedStartYear);
    }

    if (normalizedSearch !== location.search) {
      history.replace({ pathname: location.pathname, search: normalizedSearch });
    }
  }, [
    activeSchoolYearStartYear,
    history,
    isAuthenticated,
    location.pathname,
    location.search,
    normalizedSearch,
    normalizedStartYear,
    setActiveSchoolYearStartYear,
  ]);

  const handleSchoolYearChange = (startYear) => {
    history.replace({
      pathname: location.pathname,
      search: buildSearchWithSchoolYear(location.search, startYear),
    });
  };

  const handleSwitchContext = () => {
    setSwitchError("");
    return getLoginContexts()
      .then(async (choice) => {
        if (calendarHasUnsavedChanges.current && !(await confirm(tr("manage:youHaveUnsavedCalendarChangesDiscardThemAndSwitch_250")))) return;
        setSwitchChoice(choice);
      })
      .catch(error => setSwitchError(error.message));
  };

  const handleSwitchSelect = context => (
    selectLoginContext(switchChoice.contextChoiceToken, context.contextKey)
      .then((selectedUser) => {
        rememberLastSelectedContextKey(switchChoice.accountId, context.contextKey);
        setSwitchChoice(null);
        if (selectedUser.role === ROLE.CLIENT) {
          history.replace("/client");
        } else {
          history.replace("/manage");
        }
      })
      .catch((error) => {
        clearLastSelectedContextKey(switchChoice.accountId);
        setSwitchError(error.message);
      })
  );

  return (
    <>{confirmation}<div >
      <Helmet>
        <title>{tr("manage:extraEnglishManagement_251")}</title>
      </Helmet>

      { (!user || user.role === ROLE.GUEST) && location.pathname === "/manage/password-reset" && (
        <>
          <HeaderNav />
          <div className="quiz-page fixed-header-page row m-0 px-0 pb-0 d-flex justify-content-center align-items-center">
            <div className="col-lg-8 col-11" >
              <PasswordResetRequest />
            </div>
          </div>
        </>
      )}

      { (!user || user.role === ROLE.GUEST) && location.pathname.startsWith("/manage/password-reset/") && (
        <>
          <HeaderNav />
          <div className="quiz-page fixed-header-page row m-0 px-0 pb-0 d-flex justify-content-center align-items-center">
            <div className="col-lg-8 col-11" >
              <Route path="/manage/password-reset/:token" component={PasswordResetConfirm} />
            </div>
          </div>
        </>
      )}

      { (!user || user.role === ROLE.GUEST) && location.pathname !== "/manage/password-reset" && !location.pathname.startsWith("/manage/password-reset/") && (
        <>
          <HeaderNav />
          <div className="quiz-page fixed-header-page manage-login-page row m-0 px-0 pb-0 d-flex justify-content-center">
            <div className="col-lg-8 col-11" >
              {location.state && location.state.passwordResetMessage && (
                <div className="alert alert-info" role="alert">{location.state.passwordResetMessage}</div>
              )}
              <Login />
            </div>
          </div>
        </>
      )}

      { hasUnsupportedRole && (
        <>
          <HeaderNav />
          <div className="manage-container fixed-header-page row m-0 d-flex justify-content-center">
            <div className="col-lg-10 col-11 mt-lg-5 mt-3">
              <div className="alert alert-danger manage-text" role="alert">{tr("manage:noPermission_252")}</div>
              <button
                type="button"
                className="btn manage-red-button manage-button"
                onClick={signOut}
              >{tr("common:signOut")}</button>
            </div>
          </div>
        </>
      )}

      { isAuthenticated && (
        <TeacherAccessProvider user={user}><SupportReportsProvider user={user}>
          {isSchoolYearHydrated && (
          <div className="manage-shell">
            <ManageNav
              items={roleMenuItems}
              brandName={user.schoolName || "Extra English"}
              signOut={signOut}
              currentSearch={location.search}
              activeSchoolYearStartYear={activeSchoolYearStartYear}
              schoolYearOptions={selectorOptions}
              onActiveSchoolYearChange={handleSchoolYearChange}
              permissionsRoute={canManagePermissions(user) ? "/manage/permissions" : null}
              settingsRoute={canManageSettings(user) ? "/manage/settings" : null}
              canSwitchContext={Boolean(user.canSwitchContext)}
              onSwitchContext={handleSwitchContext}
              contextSwitching={Boolean(switchChoice)}
            />
            {switchChoice && (
            <div className="manage-container fixed-header-page row m-0 d-flex justify-content-center">
              <div className="col-lg-10 col-11 mt-lg-5 mt-3">
                <ContextChoice
                  contexts={switchChoice.availableContexts || []}
                  onSelect={handleSwitchSelect}
                  error={switchError}
                  onCancel={() => {
                    setSwitchChoice(null);
                    setSwitchError("");
                  }}
                />
              </div>
            </div>
          )}
          {!switchChoice && (
            <main className="manage-container manage-shell-content row m-0 d-flex">
              <div className={`manage-content ${isDashboardRoute ? "manage-dashboard-content" : "mt-lg-5 mt-3"}`} id="parent" >
                {!canAccessCurrentPath ? (
                  <div className="alert alert-danger manage-text" role="alert">{tr("manage:noPermission_252")}</div>
                ) : (
                  <Switch>
                    <Route path="/manage/support" component={SupportRoutes} />
                    <Route
                      path="/manage"
                      render={() => (
                        <Dashboard
                          key={JSON.stringify([user.id, user.schoolId, user.activeContextKey])}
                          userId={user.id}
                          userRole={effectivePermissionRole}
                          userTeacherId={user.teacherId}
                          onActiveSchoolYearChange={handleSchoolYearChange}
                        />
                      )}
                      exact
                    />
                    <Route
                      path="/manage/people"
                      render={() => (
                        <People
                          activeSchoolYearStartYear={activeSchoolYearStartYear}
                          canReadClients={canReadClients(user)}
                          canReadTeachers={canReadTeachers(user)}
                        />
                      )}
                      exact
                    />
                    <Route
                      path="/manage/students"
                      render={() => (
                        <People
                          initialTab={PEOPLE_TABS.STUDENTS}
                          activeSchoolYearStartYear={activeSchoolYearStartYear}
                          canReadClients={canReadClients(user)}
                          canReadTeachers={canReadTeachers(user)}
                        />
                      )}
                      exact
                    />
                    <Route
                      path="/manage/clients"
                      render={() => (
                        <People
                          initialTab={PEOPLE_TABS.CLIENTS}
                          activeSchoolYearStartYear={activeSchoolYearStartYear}
                          canReadClients={canReadClients(user)}
                          canReadTeachers={canReadTeachers(user)}
                        />
                      )}
                      exact
                    />
                    <Route
                      path="/manage/teachers"
                      render={() => (
                        <People
                          initialTab={PEOPLE_TABS.TEACHERS}
                          activeSchoolYearStartYear={activeSchoolYearStartYear}
                          canReadClients={canReadClients(user)}
                          canReadTeachers={canReadTeachers(user)}
                        />
                      )}
                      exact
                    />
                    <Route path="/manage/teacher/:id" component={Teacher} />
                    <Route path="/manage/client/:id/ledger" component={ClientLedger} />
                    <Route path="/manage/client/:id/notifications" component={ClientNotificationHistory} />
                    <Route
                      path="/manage/client/:id"
                      render={props => (effectivePermissionRole === "TEACHER"
                        ? <TeacherPeopleProfile kind="client" /> : <Client {...props} />)}
                    />
                    <Route
                      path="/manage/student/:id"
                      render={props => (effectivePermissionRole === "TEACHER"
                        ? <TeacherPeopleProfile kind="student" /> : <Student {...props} />)}
                    />
                    <Route path="/manage/class/:classId/lesson/:lessonIndex/attendance" component={AttendancePage} />
                    <Route path="/manage/class/:id" component={SchoolClass} />
                    <Route path="/manage/classes" component={SchoolClasses} exact />
                    <Route path="/manage/book/:id" component={Book} />
                    <Route path="/manage/books" component={Books} exact />
                    <Route path="/manage/product/:id" component={Product} />
                    <Route path="/manage/products" component={Products} exact />
                    <Route path="/manage/enrollment/:id" component={Enrollment} />
                    <Route path="/manage/enrollments" component={Enrollments} exact />
                    <Route path="/manage/reports/client-settlements" component={ClientSettlementsReport} exact />
                    <Route path="/manage/reports" render={() => <AttendanceReport userId={user.id} userRole={effectivePermissionRole} />} exact />
                    <Route path="/manage/permissions" component={Permissions} exact />
                    <Route
                      path="/manage/settings/school-calendar"
                      exact
                      render={() => (
                        <SchoolCalendarEditor schoolYearStart={activeSchoolYearStartYear} contextKey={calendarContextKey(user)} onUnsavedChange={handleCalendarUnsavedChange} />
                      )}
                    />
                    <Route
                      path="/manage/settings"
                      exact
                      render={() => <Settings user={user} calendarContext={calendarContextKey(user)} schoolYearStart={activeSchoolYearStartYear} />}
                    />
                    <Route
                      path="/manage/school-calendar"
                      exact
                      render={() => <SchoolCalendarReadPage schoolYearStart={activeSchoolYearStartYear} contextKey={calendarContextKey(user)} />}
                    />
                    <Route path="/manage/login" component={Login} exact />
                    <Route component={NotFoundPage} exact />
                  </Switch>
                )}
              </div>
            </main>
            )}
          </div>
          )}
        </SupportReportsProvider>
        </TeacherAccessProvider>
      )}

    </div></>
  );
}

ManagementPage.propTypes = {
  user: PropTypes.object.isRequired,
  signOut: PropTypes.func.isRequired,
  checkToken: PropTypes.func.isRequired,
  getLoginContexts: PropTypes.func,
  selectLoginContext: PropTypes.func,
  activeSchoolYearStartYear: PropTypes.number.isRequired,
  setActiveSchoolYearStartYear: PropTypes.func.isRequired,
};

ManagementPage.defaultProps = {
  getLoginContexts: () => Promise.resolve(null),
  selectLoginContext: () => Promise.resolve(null),
};

const mapStateToProps = state => ({
  user: state.user,
  activeSchoolYearStartYear: state.settings.activeSchoolYearStartYear,
});

const mapDispatchToProps = {
  signOut: userActions.signOut,
  checkToken: userActions.checkToken,
  getLoginContexts: userActions.getLoginContexts,
  selectLoginContext: userActions.selectLoginContext,
  setActiveSchoolYearStartYear: settingsActions.setActiveSchoolYearStartYear,
};


export default connect(mapStateToProps, mapDispatchToProps)(ManagementPage);
